import dynamic from "next/dynamic";
import Quill from "quill";
import { ReactQuillProps } from "react-quill";
import Skeleton from "@ui-kit/feedback/Skeleton/Skeleton.web";
import { useRef, useState, useMemo, useCallback } from "react";
import Box from "@ui-kit/layout/Box/Box.web";
import { RichTextEditorPropsType } from "./RichTextEditor.props";
import ImageInsertModal from "./ImageInsertModal/ImageInsertModal.web";
import { ImageInsertData } from "./ImageInsertModal/ImageInsertModal.props";
import "./RichTextEditor.css";

const toolBarHeight = 57.86;
const editorHeight = 298.86;

const gray400 = "var(--chakra-colors-gray-400)";

// using dynamic import because we can't render on server as this component
// uses the dom. During load we render a matching placeholder to prevent layout shift
const ReactQuill = dynamic(
  async () => {
    const { default: RQ } = await import("react-quill");
    // forwards props and refs to the internal component
    // more information here: https://github.com/zenoamaro/react-quill/issues/642
    return Object.assign((props: ReactQuillProps) => <RQ {...props} />, {
      displayName: "RQ",
    });
  },
  {
    ssr: false,
    loading: function renderPlaceholder() {
      return (
        <Box
          borderRadius="sm"
          minHeight={`${editorHeight}px`}
          border={`1px solid ${gray400}`}
        >
          <Skeleton height={`${toolBarHeight}px`} />
        </Box>
      );
    },
  }
);

interface HTMLElementWithQuill extends HTMLElement {
  __quill?: Quill;
}

const RichTextEditor = (props: RichTextEditorPropsType): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    updateValue,
    toolbarOptions,
    isInvalid,
    enableCustomImageHandler,
    id,
    value,
    placeholder,
  } = props;
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  const handleImageInsert = (imageData: ImageInsertData): void => {
    let quill = null;

    if (containerRef.current) {
      const editorElement = containerRef.current.querySelector(".ql-editor");

      if (editorElement) {
        const quillContainer = editorElement.parentElement;

        if (
          quillContainer &&
          // eslint-disable-next-line no-underscore-dangle
          (quillContainer as HTMLElementWithQuill).__quill
        ) {
          // eslint-disable-next-line no-underscore-dangle
          quill = (quillContainer as HTMLElementWithQuill).__quill;
        }
      }
    }

    if (quill) {
      const range = quill.getSelection();
      const position = range ? range.index : quill.getLength();

      let imageHtml = `<img src="${imageData.src}"`;
      if (imageData.alt) {
        imageHtml += ` alt="${imageData.alt}"`;
      }
      if (imageData.width) {
        imageHtml += ` width="${imageData.width}"`;
      }
      if (imageData.height) {
        imageHtml += ` height="${imageData.height}"`;
      }
      imageHtml += " />";

      quill.clipboard.dangerouslyPasteHTML(position, imageHtml);
      quill.setSelection(position + 1, 0);
    }
  };

  const imageHandler = useCallback(() => {
    setIsImageModalOpen(true);
  }, []);

  const modules = useMemo(() => {
    if (enableCustomImageHandler) {
      return {
        toolbar: {
          container: toolbarOptions,
          handlers: {
            image: imageHandler,
          },
        },
      };
    }
    return {
      toolbar: toolbarOptions,
    };
  }, [enableCustomImageHandler, toolbarOptions, imageHandler]);

  const memoizedProps = useMemo(
    () => ({
      id,
      value,
      placeholder,
      onChange: updateValue,
      modules,
    }),
    [id, value, placeholder, updateValue, modules]
  );

  return (
    <>
      <Box
        ref={containerRef}
        width="100%"
        className="rich-text-editor"
        data-invalid={isInvalid || undefined}
      >
        <ReactQuill {...memoizedProps} />
      </Box>
      {enableCustomImageHandler && (
        <ImageInsertModal
          isOpen={isImageModalOpen}
          onClose={() => setIsImageModalOpen(false)}
          onInsert={handleImageInsert}
        />
      )}
    </>
  );
};

export default RichTextEditor;
